<template>
  <div id="SectionHero">
    <UIContainer class="SectionHero__container">
      <template #left>
        <!-- Trust Badge -->
        <div class="SectionHero__trust-badge">
          <UIIcon class="SectionHero__trust-icon" name="checkCircleOutline" />
          <span class="SectionHero__trust-text">Trusted by 100+ companies</span>
        </div>

        <!-- Main Headline -->
        <SectionTitle titleTag="h1">
          <template #title>
            <span class="SectionHero__title-highlight">Your fast track</span> to
            Ecovadis certification
          </template>
        </SectionTitle>

        <!-- Description -->
        <p class="SectionHero__description">
          We turn your existing data, policies, and supplier practices into a
          complete, audit-ready submission. No fluff, no guesswork; just the
          <strong>shortest path</strong> from today's gaps to a higher score.
        </p>
        <SectionSocialProof />

        <!-- CTA Button -->
        <div class="SectionHero__cta">
          <UIButton size="xl" theme="primary" :to="{ name: 'Contact' }">
            Book a demo
          </UIButton>
        </div>
      </template>

      <template #right>
        <div class="SectionHero__image-container">
          <img
            class="SectionHero__image"
            src="./assets/hero/bg.avif"
            alt="Ecovadis certification badges and sustainability ratings"
            fetchpriority="high"
            loading="eager"
          />
          <!-- <img
            class="SectionHero__image-badges"
            src="./assets/hero/badges.png"
            alt="Ecovadis certification badges and sustainability ratings"
          /> -->
        </div>
      </template>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import SectionSocialProof from '@/views/components/sections/SectionSocialProof.vue'
import UIButton from '@/views/components/ui/buttons/UIButton.vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
</script>

<style lang="scss" scoped>
#SectionHero {
  .SectionHero__container {
    padding: 2rem 2rem 3rem;
    position: relative;

    @include md {
      padding: 2.875rem 3rem 6rem;
    }
  }

  .SectionHero__image-container {
    display: none;
    position: relative;

    @include md {
      display: block;
    }
  }

  .SectionHero__image {
    object-fit: cover;
    object-position: center;
    width: 100%;
  }

  .SectionHero__image-badges {
    position: absolute;
    bottom: -114px;
    right: -75px;
  }

  .SectionHero__trust-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background-color: $newcolor-primary-pale;
    color: $color-ui-brand-700;
    padding: 0.5rem 1rem;
    border-radius: 2rem;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 1.5rem;
    border: 1px solid $color-ui-brand-200;
  }

  .SectionHero__trust-icon {
    --size: 1rem;
    --color: #{$newcolor-primary-main};
  }

  .SectionHero__title-highlight {
    background: linear-gradient(45deg, #023025 0%, #04785e 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  .SectionHero__description {
    @include font-size-18px();
    color: $color-ui-gray-cool-600;
    line-height: 1.6;
    margin-bottom: 2rem;
    max-width: 32rem;

    strong {
      font-weight: 600;
      color: $color-ui-gray-cool-900;
    }
  }

  .SectionHero__clients {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .SectionHero__client-icons {
    display: flex;
    gap: 0.25rem;
  }

  .SectionHero__client-icon {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    border: 2px solid $color-white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &--pink {
      background-color: #ff6b9d;
    }

    &--purple {
      background-color: #8b5cf6;
    }

    &--green {
      background-color: #10b981;
    }
  }

  .SectionHero__clients-text {
    @include font-size-16px();
    color: $color-ui-gray-cool-600;
    font-weight: 500;
  }

  .SectionHero__rating {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .SectionHero__stars {
    color: #fbbf24;
    font-size: 1.125rem;
  }

  .SectionHero__rating-score {
    @include font-size-16px();
    color: $color-ui-gray-cool-900;
    font-weight: 600;
  }

  .SectionHero__cta {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2rem;

    @include md {
      justify-content: flex-start;
    }
  }
}
</style>
